import React, { useState } from 'react'
import ShareIcon from '@mui/icons-material/Share'
import Typography from '@mui/material/Typography'
import { GlassButton } from './Glass'
import { SxProps, Theme } from '@mui/material/styles'
import { ButtonProps } from '@mui/material/Button'
import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
import { getShareOrigin } from 'src/utils/helpers/share'

type ShareButtonProps = {
  listId?: number | string
  pollId?: number | string
  score?: number
  listTitle: string
  onClick?: () => void
  onCreate?: () => Promise<number | undefined>
  result?: 'win' | 'loss' | 'active'
  disabled?: boolean
  shareType?: 'list' | 'poll'
  pollShareMode?: 'vote' | 'played'
  fullWidth?: boolean
  size?: ButtonProps['size']
  variant?: ButtonProps['variant']
  sx?: SxProps<Theme>
}

export function ShareButton({
  listId,
  pollId,
  score = 0,
  listTitle,
  onCreate,
  onClick,
  result,
  disabled,
  shareType = 'list',
  pollShareMode = 'vote',
  fullWidth = false,
  size = 'medium',
  variant = 'contained',
  sx,
}: ShareButtonProps): JSX.Element {
  const [copied, setCopied] = useState(false)
  const shareOrigin = getShareOrigin()

  const slugify = (value: string): string =>
    String(value || '')
      .toLowerCase()
      .replace(/['"`’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 72)

  const normalizeNumericId = (value: string | number): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return Math.floor(value)
    }
    const str = String(value || '').trim()
    const match = str.match(/\d+/)
    if (!match) return undefined
    const parsed = Number(match[0])
    if (!Number.isFinite(parsed) || parsed <= 0) return undefined
    return Math.floor(parsed)
  }
  const buildShareUrl = (id: string | number) => {
    const normalizedId = normalizeNumericId(id)
    if (!normalizedId) return ''
    const slug = slugify(listTitle) || (shareType === 'poll' ? 'poll' : 'list')
    if (shareType === 'poll') {
      const params = new URLSearchParams()
      params.set('m', pollShareMode)
      if (pollShareMode === 'played' && Number.isFinite(score) && score > 0) {
        params.set('s', String(Math.max(0, Math.floor(score))))
      }
      if (pollShareMode === 'played' && result === 'loss') {
        params.set('r', 'loss')
      }
      return `${shareOrigin}/share/poll/${normalizedId}-${slug}?${params.toString()}`
    }
    const params = new URLSearchParams()
    params.set('s', String(Math.max(0, Math.floor(score))))
    if (result === 'loss') {
      params.set('r', 'loss')
    }
    return `${shareOrigin}/share/list/${normalizedId}-${slug}?${params.toString()}`
  }
  const shareText =
    shareType === 'poll'
      ? pollShareMode === 'played'
        ? result === 'loss'
          ? `I couldn't rank the community poll “${listTitle}” in ${Math.max(
              1,
              Math.floor(score) || 5
            )} guesses. Can you?`
          : `I ranked the community poll “${listTitle}” in ${Math.max(
              1,
              Math.floor(score) || 5
            )} guesses. Can you do better?`
        : `Join the poll: Vote on “${listTitle}” with the community.`
      : result === 'loss'
        ? `I couldn't rank "${listTitle}" in 5 guesses. Can you?`
        : `I ranked “${listTitle}” in ${Math.max(1, Math.floor(score) || 1)} guesses. Can you do better?`
  async function handleShare() {
    onClick?.()
    // If no list id, save list and
    let useId = shareType === 'poll' ? pollId : listId
    if (!useId && onCreate) {
      useId = await onCreate()
    }
    if (!useId) return

    const shareUrl = buildShareUrl(useId)
    if (!shareUrl) return
    try {
      if (Capacitor.isNativePlatform()) {
        await Share.share({
          url: shareUrl,
          dialogTitle: 'Share',
        })
        return
      }

      if (navigator.share) {
        await navigator.share({
          url: shareUrl,
        })
        return
      }

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    } catch (err) {
      if (navigator.clipboard && window.isSecureContext) {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareUrl)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    }
  }

  return (
    <div
      style={{
        position: 'relative',
        display: fullWidth ? 'block' : 'inline-block',
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      <GlassButton
        onClick={handleShare}
        variant={variant}
        disabled={disabled}
        fullWidth={fullWidth}
        size={size}
        sx={sx}
        endIcon={<ShareIcon fontSize="inherit" color="disabled" sx={{ fontSize: 17 }} />}
      >
        <Typography
          fontSize={16}
          fontWeight={700}
          sx={{
            color: 'inherit',
            textShadow: 'none',
          }}
        >
          Share
        </Typography>
      </GlassButton>

      {copied && (
        <div
          style={{
            position: 'absolute',
            top: '-2rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(15,23,42,0.9)',
            color: '#fff',
            padding: '0.3rem 0.7rem',
            borderRadius: '999px',
            fontSize: '0.8rem',
            whiteSpace: 'nowrap',
          }}
        >
          Link copied!
        </div>
      )}
    </div>
  )
}
